import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, IconButton, Popover, Stack, Typography } from '@mui/material'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import { QRCodeSVG } from 'qrcode.react'
import { useSmartTranslation } from '../../../../../../generic/hooks/useSmartTranslation'
import { buildQrPayload } from '../../../../../../generic/qr/qrPayload'
import QrCodeGeneratorDialog, { buildAvailableFields } from '../../../../../../generic/qr/QrCodeGeneratorDialog'
import { OVERLINE_SX } from './JobDetailPrimitives'

// Historia 160 (CU11, RN42/RN43, D15/D28, mock 7) — "QR DE LA ORDEN".
//
// El QR fijo lo ve TODO perfil MOS y no llama a nada: se construye en el cliente con el formato de
// MIC (`buildQrPayload`) y lo pinta el mismo `QRCodeSVG` que el generador. "Generar QR" abre el
// generador de siempre, y solo existe con `GENERATE_QR_CODE` (la autoridad que exigen `qrTemplates/*`,
// T7): sin ella el botón no está, ni siquiera deshabilitado.

/** D15: la carga útil MIC-QR de la orden (identidad + código). */
export const orderQrPayload = (fabricationOrder = {}) => buildQrPayload({
  entityType: 'FabricationOrder',
  id: fabricationOrder.id,
  entries: [{ field: 'fabricationOrder', value: fabricationOrder.code || '' }]
})

const JobDetailQrPopover = ({ fabricationOrder = {}, partReference = {}, canGenerate = false }) => {
  const { t_ } = useSmartTranslation()
  const [anchor, setAnchor] = useState(false)
  const [generatorOpen, setGeneratorOpen] = useState(false)
  const payload = orderQrPayload(fabricationOrder)

  // La instancia y los campos se congelan al pulsar "Generar QR": el refresco del detalle no altera
  // un generador abierto (RN46).
  const [generatorSnapshot, setGeneratorSnapshot] = useState({ instance: {}, availableFields: [] })
  const qrColumns = useMemo(() => [
    { field: 'fabricationOrder', name: t_('Fabrication Order') },
    { field: 'description', name: t_('Description') },
    { field: 'partReference.code', name: t_('Part Reference') },
    { field: 'deliveryDate', name: t_('Delivery date'), type: 'dateTimePicker' }
  ], [t_])

  const openGenerator = () => {
    const instance = {
      id: fabricationOrder.id,
      fabricationOrder: fabricationOrder.code || '',
      description: fabricationOrder.description || '',
      deliveryDate: fabricationOrder.deliveryDate || '',
      partReference: { code: partReference.code || '' }
    }
    setGeneratorSnapshot({ instance, availableFields: buildAvailableFields(qrColumns, instance) })
    setAnchor(false)
    setGeneratorOpen(true)
  }

  return (
    <>
      <IconButton
        aria-label={t_('Order QR')}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, width: 48, height: 48 }}
      >
        <QrCode2Icon />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor || undefined}
        onClose={() => setAnchor(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { bgcolor: 'background.card', border: 1, borderColor: 'divider', borderRadius: 2, mt: 1 } } }}
      >
        <Stack alignItems='center' spacing={1.5} sx={{ p: 2.5, minWidth: 260 }} data-testid='order-qr-popover'>
          <Typography component='h3' sx={OVERLINE_SX}>{t_('Order QR')}</Typography>
          <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: 2, lineHeight: 0 }}>
            <QRCodeSVG value={payload} size={200} level='Q' data-testid='order-qr' data-payload={payload} />
          </Box>
          <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem' }}>{fabricationOrder.code || ''}</Typography>
          {canGenerate
            ? <Button variant='outlined' onClick={openGenerator} sx={{ minHeight: 48 }}>{t_('Generate QR code')}</Button>
            : null}
        </Stack>
      </Popover>
      {canGenerate && generatorOpen
        ? (
          <QrCodeGeneratorDialog
            open={generatorOpen}
            onClose={() => setGeneratorOpen(false)}
            entityType='FabricationOrder'
            allowPrinting={false}
            entityLabel={t_('Fabrication Order')}
            instance={generatorSnapshot.instance}
            subtitle={generatorSnapshot.instance.fabricationOrder}
            availableFields={generatorSnapshot.availableFields}
          />
          )
        : null}
    </>
  )
}

JobDetailQrPopover.propTypes = {
  fabricationOrder: PropTypes.object,
  partReference: PropTypes.object,
  canGenerate: PropTypes.bool
}

export default JobDetailQrPopover
